import {
  createRootRoute,
  HeadContent,
  Outlet,
  Scripts,
  useRouterState,
} from "@tanstack/react-router";
import { type ReactNode, Suspense } from "react";
import { type AppLocale, DEFAULT_LOCALE, localeDirection } from "shared/Locale";
import AppIntlProvider from "src/components/app/AppIntlProvider/AppIntlProvider";
import CookieConsentBanner from "src/components/app/CookieConsentBanner/CookieConsentBanner";
import GlobalTransitions from "src/components/app/GlobalTransition/GlobalTransitions";
import GoogleAnalytics from "src/components/app/GoogleAnalytics/GoogleAnalytics";
import PortalLocale from "src/components/app/PortalLocale/PortalLocale";
import { DismissGuardHost } from "src/components/common/DismissGuard/DismissGuard";
import RouteProgress from "src/components/common/RouteProgress/RouteProgress";
import Toasts from "src/components/common/Toasts/Toasts";
import { RequestCookieConsentContext } from "src/lib/CookieConsent";
import { AuthProvider } from "src/lib/contexts/AuthProvider";
import { FONT_PRELOADS, ROOT_STYLESHEETS } from "src/lib/DocumentAssets";
import { useDocumentHydrated } from "src/lib/DocumentHydrationMarker";
import { metadataHead } from "src/lib/Metadata";
import { resolvePortalLocale, useRoutePortalLocale } from "src/lib/PortalLocale";
import { ROOT_METADATA } from "src/lib/Seo";
import { NotFoundPage, RouteErrorPage } from "src/routes/-components/RouteBoundaries";
import { getAuthState } from "src/server/functions/AuthState";
import "src/serverComponentStyles";

/** Marks the end of the page's markup, for the render-blocking `expect` link in the root head. */
const PAGE_RENDERED_ID = "page-rendered";

export const Route = createRootRoute({
  // The signed-in user, for every layout. Loaded once per document and again only when something
  // calls `router.invalidate()` (sign in/out, `useAppRouter().refresh()`). `renderedAt` is the
  // document's render time, shared with the browser for hydration (AppIntlProvider).
  loader: async () => ({ ...(await getAuthState()), renderedAt: Date.now() }),
  staleTime: Number.POSITIVE_INFINITY,
  head: () => {
    const head = metadataHead(ROOT_METADATA, { template: false });
    return {
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover" },
        ...head.meta,
      ],
      links: [
        ...FONT_PRELOADS.map((href) => ({
          rel: "preload",
          as: "font",
          type: "font/woff2",
          href,
          crossOrigin: "anonymous" as const,
        })),
        ...ROOT_STYLESHEETS.map((href) => ({ rel: "stylesheet", href })),
        // Hold the first paint until the page's markup is parsed (PAGE_RENDERED_ID, after the
        // routes). Otherwise browsers paint a large page part-parsed, at the top, and a reload of a
        // scrolled page flashes before its scroll position is restored. Ignored where unsupported.
        { rel: "expect", href: `#${PAGE_RENDERED_ID}`, blocking: "render" },
        ...head.links,
      ],
    };
  },
  shellComponent: RootDocument,
  component: RootComponent,
  errorComponent: RouteErrorPage,
  notFoundComponent: NotFoundPage,
});

function RootComponent() {
  // `<html data-hydrated>` once the page has hydrated, for the e2e specs (src/lib/DocumentHydration).
  useDocumentHydrated();
  return <Outlet />;
}

/** The `_authed` layout's browser language: set only on portal pages (src/routes/_authed.tsx). */
const usePortalBrowserLocale = () =>
  useRouterState({
    select: (state) =>
      (
        state.matches.find((match) => match.routeId === "/_authed")?.loaderData as
          | { browserLocale?: AppLocale }
          | undefined
      )?.browserLocale,
  });

/** Translates the document into the portal's language on portal pages; English elsewhere. */
function RouteLocale({ children }: { children: ReactNode }) {
  return (
    <PortalLocale locale={useRoutePortalLocale(usePortalBrowserLocale())}>{children}</PortalLocale>
  );
}

function RootDocument({ children }: { children: ReactNode }) {
  // Optional: the shell also renders when the root loader failed.
  const renderedAt = Route.useLoaderData({ select: (data) => data?.renderedAt });
  const cookieConsent = Route.useLoaderData({ select: (data) => data?.cookieConsent });
  const authState = Route.useLoaderData({ select: (data) => data?.authState });
  // The server-rendered language; RouteLocale keeps it current in the browser.
  const browserLocale = usePortalBrowserLocale();
  const locale = browserLocale
    ? resolvePortalLocale(authState?.user?.locale, browserLocale)
    : DEFAULT_LOCALE;
  return (
    <html lang={locale} dir={localeDirection(locale)}>
      <head>
        <HeadContent />
      </head>
      <body>
        <AuthProvider initialAuthState={authState}>
          <AppIntlProvider renderedAt={renderedAt}>
            <RouteLocale>
              <RequestCookieConsentContext value={cookieConsent}>
                {children}
                <div id={PAGE_RENDERED_ID} hidden={true} />
                <div data-modal-container={true} />
                <div data-popover-container={true} />
                <Suspense fallback={null}>
                  <GlobalTransitions />
                </Suspense>
                <Toasts />
                <CookieConsentBanner />
                <GoogleAnalytics />
                <RouteProgress />
                <DismissGuardHost />
              </RequestCookieConsentContext>
            </RouteLocale>
          </AppIntlProvider>
        </AuthProvider>
        <Scripts />
      </body>
    </html>
  );
}
